import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import { orderBy } from 'lodash';
import { ProgramConsumer } from '../../contexts/ProgramContext';
import ProgramCard from './components/ProgramCard';
import { breaks, placeholders, conferenceOpening } from './constants';

import './Program.scss';

export const Program = ({
  loadProgram,
  talks,
  selectedTalk,
  speakers,
  setSelectedTalk,
  isLoading
}) => {
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    loadProgram();
    if (history.action !== 'POP') {
      window.scrollTo(0, 0);
      setSelectedTalk('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderProgramRow = (talksInRow, time) => {
    const placeholdersExist = placeholders.find(placeholder => placeholder.startTime === time);
    let slots = talksInRow.length > 0 ? [...talksInRow] : [];
    if (placeholdersExist) {
      slots = [...talksInRow, ...placeholdersExist.groupedTalks];
    }
    const orderedSlots = slots.length > 0 ? orderBy(slots, ['trackInDay'], 'asc') : [];

    const columnWidth = location.pathname === '/program' ? 'is-2' : '';
    const columnClasses = orderedSlots.length === 1 ? 'is-12' : `is-12-touch ${columnWidth}`;

    if (orderedSlots[0].isBreak) {
      return <div className="break-slot column is-12">{orderedSlots[0].title}</div>;
    }
    return orderedSlots.map(slot => {
      let speakerNames = [];
      const color = location.pathname === '/program' ? slot.trackInDay : 'workshop';
      if (!slot.isBreak && !slot.isConferenceOpening && slot.speakers) {
        slot.speakers.forEach(speakerId => {
          const foundSpeaker = speakers.find(sp => sp.id === speakerId) || {};
          if (foundSpeaker && foundSpeaker.name) {
            speakerNames.push(foundSpeaker);
          }
        });
      } else if (slot.isConferenceOpening) {
        speakerNames = [...slot.speakers];
      }

      return (
        <div key={slot.id || slot.title} className={`column ${columnClasses}`}>
          <ProgramCard
            key={slot.id || slot.title}
            color={color}
            id={slot.id}
            onCardClick={setSelectedTalk}
            selectedTalk={selectedTalk}
            speakers={speakerNames}
            talk={slot}
            tileRole={slot.tileRole || ''}
          />
        </div>
      );
    });
  };

  const renderProgram = () => {
    let filteredTalks = [];
    let designThinkingWorkshop = null;
    if (location.pathname === '/program') {
      filteredTalks = talks.filter(talk => talk.track === 'Talk');
      filteredTalks.push(conferenceOpening);
    } else {
      filteredTalks = talks.filter(talk => talk.track !== 'Talk');
      designThinkingWorkshop = filteredTalks.find(
        talk => talk.id === '81deeef0-b68a-4ed5-bfc3-d95a31be0ae1'
      );
    }

    const uniqueStartTimes = [...new Set(filteredTalks.map(item => item.startTime))];
    const talksByTime = [];

    // eslint-disable-next-line no-restricted-syntax
    for (const startTime of uniqueStartTimes) {
      const groupedTalksByTime = filteredTalks.filter(talk => talk.startTime === startTime);
      if (
        !groupedTalksByTime.find(talk => talk.id === '81deeef0-b68a-4ed5-bfc3-d95a31be0ae1') &&
        location.pathname === '/workshops'
      ) {
        const enchancedDesignThinking = { ...designThinkingWorkshop };
        enchancedDesignThinking.tileRole = 'CLOSED';
        groupedTalksByTime.push(enchancedDesignThinking);
      }
      talksByTime.push({ startTime, groupedTalks: groupedTalksByTime });
    }

    const mergedTalksAndBreaks =
      location.pathname === '/program' ? [...breaks, ...talksByTime] : [...talksByTime];
    const orderedTalksAndBreaks = orderBy(mergedTalksAndBreaks, ['startTime'], 'asc');

    return (
      <div className="program-wrapper is-full">
        {filteredTalks.length > 0 ? (
          orderedTalksAndBreaks.map(row => (
            <div key={row.startTime} className="program-row">
              <div className="program-row-time">{row.startTime}</div>
              <div className="columns program-row-talks">
                {renderProgramRow(row.groupedTalks, row.startTime)}
              </div>
            </div>
          ))
        ) : (
          <h2 className="subtitle">There is no program at this time, stay tuned.</h2>
        )}
      </div>
    );
  };

  return (
    <section className="section" id="program-section">
      <div className="container">
        <h1 className="">{location.pathname === '/program' ? 'PROGRAM' : 'HANDS-ON LABS'}</h1>
        <h2 className="subtitle">
          {location.pathname === '/program' ? (
            <div>
              <span className="note">
                If you were unable to join us this year or missed any of the presentations, you’ll
                now find the talk video recordings bellow, simply click on the video icon next to
                each talk (please note that some speakers didn’t give us consent to record and
                publish their talks).
              </span>
            </div>
          ) : null}
          <div>
            <span className="date">
              {location.pathname === '/workshops' ? 'Oct, 19' : 'Oct, 20'}
            </span>
            <span className="conference-day">
              {location.pathname === '/workshops' ? 'Day 1' : 'Day 2'}
            </span>
          </div>
        </h2>
        {location.pathname === '/program' ? (
          <div className="track-names-row">
            <div className="track-name">
              <div className="track-marker track0-color" />
              <div className="track-title">Future and Sustainability</div>
            </div>
            <div className="track-name">
              <div className="track-marker track1-color" />
              <div className="track-title">Extended Reality and Metaverse</div>
            </div>
            <div className="track-name">
              <div className="track-marker track2-color" />
              <div className="track-title">Applications and Architecture</div>
            </div>
            <div className="track-name">
              <div className="track-marker track3-color" />
              <div className="track-title">Data</div>
            </div>
            <div className="track-name">
              <div className="track-marker track4-color" />
              <div className="track-title">Cloud and Robotics</div>
            </div>
            <div className="track-name">
              <div className="track-marker track5-color" />
              <div className="track-title">Panel Discussions</div>
            </div>
          </div>
        ) : null}
        {/* (
          <div className="track-names-row">Hands-On Labs registration will open soon.</div>
        )} */}
        {!isLoading ? (
          renderProgram()
        ) : (
          <i className="fa fa-spinner fa-spin fa-3x fa-fw" style={{ color: '#7500c0' }} />
        )}
      </div>
    </section>
  );
};

Program.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  loadProgram: PropTypes.func.isRequired,
  selectedTalk: PropTypes.string.isRequired,
  setSelectedTalk: PropTypes.func.isRequired,
  speakers: PropTypes.arrayOf(PropTypes.any).isRequired,
  talks: PropTypes.arrayOf(PropTypes.any).isRequired
};

const ProgramContainer = (...props) => (
  <ProgramConsumer>
    {({ loadProgram, talks, selectedTalk, speakers, setSelectedTalk, isLoading }) => (
      <Program
        isLoading={isLoading}
        loadProgram={loadProgram}
        selectedTalk={selectedTalk}
        setSelectedTalk={setSelectedTalk}
        speakers={speakers}
        talks={talks}
        {...props}
      />
    )}
  </ProgramConsumer>
);

export default ProgramContainer;
